"use client";
import React from "react";
import { PlusCircle } from "lucide-react";
import { LGS } from "modules/lgs/model/lgs";
import useUserLocation from "modules/lgs/hooks/useUserLocation";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { StoreList } from "modules/lgs/components/StoreList";
import { useMapSearchParams } from "modules/common/components/map/hooks/useMapSearchParams";

export interface SearchResultStoresListProps {
  LGSsFromWotcQuery: LGS[];
  handleChange: (storeId: number) => void;
  shopCheckedState: Record<number | string, boolean>;
  selectionLimitReached?: boolean;
  compact?: boolean;
}

export function SearchResultStoresList({
  LGSsFromWotcQuery,
  handleChange,
  shopCheckedState,
  selectionLimitReached = false,
  compact = false,
}: SearchResultStoresListProps) {
  const { city: urlCity } = useMapSearchParams();
  const { searchCity, isLoading: searchCityInitialLoading } = useUserLocation();

  // URL city is already in display format from useMapSearchParams (e.g., "Venice, Italy")
  const formattedUrlCity = urlCity || null;

  // Multi-level fallback with loading state
  // Priority: URL params (IP/search) > browser geolocation > fallback
  const displayCity = searchCityInitialLoading
    ? "Loading location..."
    : formattedUrlCity || searchCity || "Nearby stores";

  const availableStores =
    LGSsFromWotcQuery?.filter((store) => !shopCheckedState[store.wotcId]) || [];

  const hasStores = LGSsFromWotcQuery?.length > 0;
  const hasAvailableStores = availableStores.length > 0;
  const allStoresSelected = hasStores && !hasAvailableStores;

  // Format stores for the StoreList component
  const formattedStores = availableStores.map((store) => ({
    id: store.wotcId,
    name: store.name,
    isTradingHub: store.isTradingHub,
  }));

  // Determine status message
  let statusMessage = "";
  if (!hasStores) {
    statusMessage = "No stores found in this area";
  } else if (allStoresSelected) {
    statusMessage = "All stores already selected!";
  } else if (selectionLimitReached) {
    statusMessage = "Remove a saved store to add another.";
  }

  return (
    <StoreList
      title={`Stores in: ${displayCity}`}
      count={LGSsFromWotcQuery?.length || 0}
      stores={formattedStores}
      emptyMessage="No stores available"
      onStoreClick={handleChange}
      renderIcon={() =>
        compact ? (
          <PlusCircle
            size={ICON_SIZE}
            className="text-success shrink-0"
            aria-hidden="true"
          />
        ) : (
          <span className="bg-success/10 text-success grid size-8 shrink-0 place-items-center rounded-full">
            <PlusCircle size={ICON_SIZE} aria-hidden="true" />
          </span>
        )
      }
      isLoading={searchCityInitialLoading}
      statusMessage={statusMessage}
      iconPosition="left"
      disabled={selectionLimitReached}
      compact={compact}
    />
  );
}

export default SearchResultStoresList;
